import React from "react";
import { TouchableOpacity, View, Text, StyleSheet, InteractionManager } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { useCallback } from "react";
import { useRealNotifications } from "../hooks/useRealNotifications";
import { useColors, ThemeColors } from "../hooks/useColors";
import { spacing } from "../theme";
import { useGuestMode } from "../contexts/GuestModeContext";

export function NotificationBell() {
  const navigation = useNavigation();
  const { unreadCount, notifications, fetchNotifications } = useRealNotifications();
  const colors = useColors();
  const styles = createStyles(colors);
  const { requireAuth } = useGuestMode();

  // Refetch notifications when the screen containing this bell comes into focus
  // This ensures the badge updates after deleting notifications on the NotificationsScreen
  // Note: Each component using useRealNotifications has its own state instance,
  // so we need to refetch here to update this component's badge
  // Use InteractionManager to defer until navigation animation completes - prevents blocking touches
  useFocusEffect(
    useCallback(() => {
      const task = InteractionManager.runAfterInteractions(() => {
        fetchNotifications();
      });
      return () => task.cancel();
    }, [fetchNotifications])
  );

  // Debug logging removed - was causing performance issues by logging on every render

  const handlePress = () => {
    // Check auth - show modal if guest
    if (!requireAuth('view notifications')) {
      return;
    }
    // @ts-ignore
    navigation.navigate("Notifications");
  };

  const handleLongPress = () => {
    // Long press handler - useful for debugging if needed
  };

  return (
    <TouchableOpacity
      style={styles.container}
      onPress={handlePress}
      onLongPress={handleLongPress}
      accessibilityLabel={`Notifications, ${unreadCount} unread`}
      accessibilityRole="button"
    >
      <Ionicons name="notifications-outline" size={26} color={colors.primary} />
      {/* Force show badge for debugging - remove this after testing */}
      {unreadCount > 0 && (
        <View style={styles.badge}>
          <Text style={styles.badgeText}>
            {unreadCount > 99 ? "99+" : unreadCount.toString()}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      position: "relative",
      padding: spacing.xs,
      marginRight: spacing.md,
    },
    badge: {
      position: "absolute",
      top: -1,
      right: 2,
      backgroundColor: colors.primary,
      borderRadius: 10,
      minWidth: 20,
      height: 20,
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 2,
      borderColor: colors.background,
    },
    badgeText: {
      color: "#000",
      fontSize: 10,
      fontWeight: "600",
    },
  });
